import React, { useState } from "react";
import {
  Box,
  Button,
  Center,
  Divider,
  Heading,
  Link,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import TextField from "../../../shared/textfield/TextField";
import AuthLayout from "../../AuthLayout";
import SocialButton from "../SocialButton/SocialButton";
import { CommonUtils } from "../../../utils/CommonUtils";
import MagicLinkDialog from "../MagicLinkDialog/MagicLinkDialog";
import PasswordDialog from "../PasswordDialog/PasswordDialog";
import { Link as ReactRouterLink } from "react-router-dom";
import useSnackBar from "../../../hooks/useSnackBar";

interface LoginPageProps {
  signInWithEmailAndPassword: (email: string, password: string) => void;
  signInWithGoogle: () => void;
  signInWithApple: () => void;
  sendMagicLink: (email: string) => void;
}

const LoginPage: React.FC<LoginPageProps> = (props: LoginPageProps) => {
  const {
    signInWithGoogle,
    signInWithApple,
    signInWithEmailAndPassword,
    sendMagicLink,
  } = props;
  const {
    isOpen: isMagicLinkDialogOpen,
    onOpen: onMagicLinkDialogOpen,
    onClose: onMagicLinkDialogClose,
  } = useDisclosure();
  const {
    isOpen: isPasswordDialogOpen,
    onOpen: onPasswordDialogOpen,
    onClose: onPasswordDialogClose,
  } = useDisclosure();
  const notify = useSnackBar();
  const [email, setEmail] = useState<string>("");
  const signIn = async () => {
    const isValid = CommonUtils.validateEmail(email);
    if (!isValid) return;
    await sendMagicLink(email);
    onMagicLinkDialogOpen();
  };
  const signInManually = (password: string) => {
    signInWithEmailAndPassword(email, password);
    onPasswordDialogClose();
  };
  const showPasswordDialog = () => {
    onMagicLinkDialogClose();
    onPasswordDialogOpen();
  };
  return (
    <>
      <AuthLayout>
        <Heading size="3xl">Welcome!</Heading>
        <Text mt="10px" fontWeight="light">
          Please sign in to continue.
        </Text>
        <Box display="flex" flexDir="column" mt="22px">
          <TextField
            value={email}
            placeholder="Email"
            name="email"
            type="email"
            onChange={(e) => setEmail(e.target.value)}
          />
          <Button
            onClick={signIn}
            alignSelf="flex-end"
            colorScheme="primary"
            color="white"
          >
            Sign in
          </Button>
        </Box>
        <Box my="22px" display="flex" alignItems="center">
          <Divider />
          <Text color="gray.400" mx="8px">
            or
          </Text>
          <Divider />
        </Box>
        <Box px={{ lg: "5rem" }}>
          <SocialButton
            text="Continue with Google"
            imageSrc="/assets/icons/google-icon.png"
            onClick={signInWithGoogle}
          />
          <SocialButton
            text="Continue with Apple"
            imageSrc="/assets/icons/apple-icon.png"
            onClick={signInWithApple}
          />
        </Box>
        <Center>
          <Text>
            First time here?
            <Link as={ReactRouterLink} to="/sign-up" color="brand" ml="4px">
              Create your profile
            </Link>
          </Text>
        </Center>
      </AuthLayout>
      <MagicLinkDialog
        resendMagicLink={() => {
          sendMagicLink(email);
          notify("success", "Magic link sent successfully.");
        }}
        onSignInManually={showPasswordDialog}
        isOpen={isMagicLinkDialogOpen}
        onClose={onMagicLinkDialogClose}
      />
      <PasswordDialog
        isOpen={isPasswordDialogOpen}
        onClose={onPasswordDialogClose}
        onSubmit={signInManually}
      />
    </>
  );
};

export default LoginPage;
